"use client";

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ProjectCard } from "@/components/ProjectCard";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'; // FIXED: Import from form.tsx
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { showSuccess, showError } from "@/utils/toast";
import { Plus, Edit } from "lucide-react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const ProjectsPage = () => {
  const { getFeatureAccess, profile, company, plan } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editingProject, setEditingProject] = useState<any>(null);
  const [showDialog, setShowDialog] = useState(false);

  // NEW: Redirect client-role users to Client Portal
  useEffect(() => {
    if (profile?.role === 'client') {
      navigate('/client-portal', { replace: true });
    }
  }, [profile, navigate]);

  // Hide project management for client-role users
  const canManageProjects = profile?.role !== 'client';

  const maxProjectsBasic = 5;

  const { data: projects, isLoading, error } = useQuery({
    queryKey: ["projects", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data, error } = await supabase
        .from("projects")
        .select("*, tasks(count)")
        .eq("company_id", company.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!company?.id && canManageProjects,
    retry: (failureCount, error) => {
      if (error.message.includes('permission')) return false;
      return failureCount < 3;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (projectData: any) => {
      const { data, error } = await supabase
        .from("projects")
        .insert({ ...projectData, company_id: company?.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setShowDialog(false);
      showSuccess("Project created successfully!");
    },
    onError: (error) => {
      showError("Failed to create project");
      console.error("Create project error:", error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (projectData: any) => {
      const { data, error } = await supabase
        .from("projects")
        .update(projectData)
        .eq("id", projectData.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setEditingProject(null);
      setShowDialog(false);
      showSuccess("Project updated successfully!");
    },
    onError: (error) => {
      showError("Failed to update project");
      console.error("Update project error:", error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("projects").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      showSuccess("Project deleted successfully!");
    },
    onError: (error) => {
      showError("Failed to delete project");
      console.error("Delete project error:", error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const data = {
      name: formData.get("name") as string,
      description: formData.get("description") as string,
      status: formData.get("status") as string,
      ...(editingProject && { id: editingProject.id }),
    };
    if (editingProject) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const canCreateMoreProjects = () => {
    if (plan === "basic") {
      return (projects?.length ?? 0) < maxProjectsBasic;
    }
    return true;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex justify-between items-center mb-6">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-3 w-1/2 mb-4" />
                <Skeleton className="h-8 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load projects. {error.message.includes('permission') ? 'You may not have permission to view projects.' : 'Please try again later.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Projects</h1>
        {canManageProjects && (
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button disabled={!canCreateMoreProjects()}>
                <Plus className="mr-2 h-4 w-4" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingProject ? "Edit Project" : "New Project"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Project Name</Label>
                  <Input id="name" name="name" defaultValue={editingProject?.name} required />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" name="description" defaultValue={editingProject?.description} />
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select name="status" defaultValue={editingProject?.status || "active"}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? "Saving..." : editingProject ? "Update Project" : "Create Project"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {plan === "basic" && (projects?.length ?? 0) >= maxProjectsBasic && (
        <Alert>
          <AlertDescription>
            You've reached the maximum of {maxProjectsBasic} projects on the Basic plan. Upgrade to create more projects.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects?.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            onEdit={canManageProjects ? (p) => { setEditingProject(p); setShowDialog(true); } : (() => {})}
            onDelete={canManageProjects ? (id) => deleteMutation.mutate(id) : (() => {})}
          />
        ))}
      </div>
      {projects?.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">No projects yet. Create one to get started!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProjectsPage;